import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { OverviewResponse, ClientSummary, SyncResult, Delta } from "../lib/types";
import { money, num, roasX, longDate, dateTime } from "../lib/format";
import { PageHeader, Loading, ErrorNote, EmptyState, Spinner } from "../components/ui";
import { RangePicker } from "../components/RangePicker";
import { StatTile } from "../components/StatTile";
import { DeltaBadge } from "../components/DeltaBadge";
import { FlagBadge } from "../components/FlagBadge";
import { IconRefresh } from "../components/icons";

type SortKey = "name" | "spend" | "conversions" | "costPerConversion" | "roas";

export function Overview() {
  const [days, setDays] = useState(7);
  const [sortKey, setSortKey] = useState<SortKey>("spend");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["overview", days],
    queryFn: () => api.get<OverviewResponse>(`/api/overview?days=${days}`),
  });

  const sync = useMutation({
    mutationFn: () => api.post<SyncResult>("/api/sync"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["overview"] }),
  });

  const rows = useMemo(() => {
    const list = [...(data?.clients ?? [])];
    list.sort((a, b) => cmp(a, b, sortKey) * (sortDir === "asc" ? 1 : -1));
    return list;
  }, [data, sortKey, sortDir]);

  const totals = useMemo(() => {
    const clients = data?.clients ?? [];
    // Spend is summed in MUR (clients may bill in different currencies).
    return {
      spendMur: clients.reduce((s, c) => s + (c.spendMur ?? 0), 0),
      unknownCurrency: clients.some((c) => c.isAdsClient && c.spendMur == null),
      conversions: clients.reduce((s, c) => s + c.conversions, 0),
      drops: clients.filter((c) => c.flag === "drop").length,
      risers: clients.filter((c) => c.flag === "rise").length,
    };
  }, [data]);

  function toggleSort(key: SortKey) {
    if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir(key === "name" ? "asc" : "desc");
    }
  }

  return (
    <div>
      <PageHeader
        title="Overview"
        subtitle={
          data ? (
            <>
              {longDate(data.window.currentStart)} – {longDate(data.window.currentEnd)}{" "}
              <span className="text-muted">vs prior {data.window.days} days</span>
            </>
          ) : (
            "All clients at a glance"
          )
        }
        actions={
          <>
            <RangePicker days={days} onChange={setDays} />
            <div className="flex items-center gap-2">
              {data?.lastSyncedAt && (
                <span className="text-xs text-muted" title={longDate(data.lastSyncedAt)}>
                  Last synced {dateTime(data.lastSyncedAt)}
                </span>
              )}
              <button
                className="btn-ghost"
                onClick={() => sync.mutate()}
                disabled={sync.isPending}
                title="Pull the latest Meta data now"
              >
                {sync.isPending ? <Spinner className="h-4 w-4" /> : <IconRefresh width={16} height={16} />}
                Refresh
              </button>
            </div>
          </>
        }
      />

      {sync.isError && <div className="mb-4"><ErrorNote message={(sync.error as Error).message} /></div>}
      {sync.data?.status === "success" && (
        <div className="mb-4 text-xs text-good">
          Synced {sync.data.accountsSynced} account(s), {sync.data.metricsUpserted} metric rows.
        </div>
      )}

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatTile
          label="Total spend"
          value={money(totals.spendMur, "MUR")}
          sub={totals.unknownCurrency ? "MUR (some currencies unset)" : "converted to MUR"}
        />
        <StatTile label="Total conversions" value={num(totals.conversions)} />
        <StatTile label="Clients dropping" value={totals.drops} sub="cost up / conversions down" />
        <StatTile label="Clients rising" value={totals.risers} sub="strong improvement" />
      </div>

      {isLoading ? (
        <Loading />
      ) : error ? (
        <ErrorNote message={(error as Error).message} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No clients yet"
          hint={
            <>
              Add clients on the <Link to="/clients" className="text-brand">Clients</Link> screen to
              start tracking performance.
            </>
          }
        />
      ) : (
        <>
        {/* Mobile: stacked cards (no sideways scroll for core metrics). */}
        <div className="space-y-2 md:hidden">
          {rows.map((c) => (
            <ClientCard key={c.clientId} c={c} />
          ))}
        </div>
        {/* Desktop: sortable table. */}
        <div className="card hidden overflow-hidden md:block">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-hairline text-left text-xs uppercase tracking-wide text-muted">
                  <Th onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>
                    Client
                  </Th>
                  <Th onClick={() => toggleSort("spend")} active={sortKey === "spend"} dir={sortDir} right>
                    Spend
                  </Th>
                  <Th
                    onClick={() => toggleSort("conversions")}
                    active={sortKey === "conversions"}
                    dir={sortDir}
                    right
                  >
                    Conversions
                  </Th>
                  <Th
                    onClick={() => toggleSort("costPerConversion")}
                    active={sortKey === "costPerConversion"}
                    dir={sortDir}
                    right
                  >
                    Cost / conv
                  </Th>
                  <Th onClick={() => toggleSort("roas")} active={sortKey === "roas"} dir={sortDir} right>
                    ROAS
                  </Th>
                  <th className="px-4 py-3 text-right">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <Row key={c.clientId} c={c} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}
    </div>
  );
}

/** Mobile card for one client — the table row reflowed for narrow screens. */
function ClientCard({ c }: { c: ClientSummary }) {
  return (
    <Link to={`/clients/${c.clientId}`} className="card block p-4 hover:bg-surface-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{c.name}</div>
          <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] text-muted">
            {!c.isAdsClient && (
              <span className="rounded bg-surface-3 px-1.5 py-0.5">no ad account</span>
            )}
            {c.services.map((s) => (
              <span key={s} className="rounded bg-surface-3 px-1.5 py-0.5">
                {s}
              </span>
            ))}
          </div>
        </div>
        <FlagBadge flag={c.flag} reasons={c.reasons} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <CardMetric
          label="Spend"
          value={money(c.spend, c.spendCurrency)}
          delta={c.deltas.spend}
          good="neutral"
        />
        <CardMetric
          label="Conversions"
          value={num(c.conversions)}
          delta={c.deltas.conversions}
          good="up"
        />
        <CardMetric
          label="Cost / conv"
          value={c.costPerConversion == null ? "—" : money(c.costPerConversion, c.spendCurrency, 2)}
          delta={c.deltas.costPerConversion}
          good="down"
        />
        <CardMetric
          label="ROAS"
          value={
            c.roas == null ? (
              c.revenueLogged ? (
                "—"
              ) : (
                "not logged"
              )
            ) : (
              <>
                {roasX(c.roas)}
                {c.roasEstimated && (
                  <span className="ml-1 text-[10px] font-normal text-warning">est</span>
                )}
              </>
            )
          }
        />
      </div>
    </Link>
  );
}

/** One labelled metric cell inside a mobile card. */
function CardMetric({
  label,
  value,
  delta,
  good,
}: {
  label: string;
  value: ReactNode;
  delta?: Delta;
  good?: "up" | "down" | "neutral";
}) {
  return (
    <div className="rounded-lg bg-surface-2 px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <span className="tnum text-sm font-medium text-ink">{value}</span>
        {delta && <DeltaBadge delta={delta} good={good} />}
      </div>
    </div>
  );
}

function Row({ c }: { c: ClientSummary }) {
  return (
    <tr className="border-b border-hairline/60 last:border-0 hover:bg-surface-2">
      <td className="px-4 py-3">
        <Link to={`/clients/${c.clientId}`} className="font-medium text-ink hover:text-brand">
          {c.name}
        </Link>
        <div className="mt-0.5 flex flex-wrap gap-1 text-[10px] text-muted">
          {!c.isAdsClient && <span className="rounded bg-surface-3 px-1.5 py-0.5">no ad account</span>}
          {c.services.map((s) => (
            <span key={s} className="rounded bg-surface-3 px-1.5 py-0.5">
              {s}
            </span>
          ))}
        </div>
      </td>
      <td className="px-4 py-3 text-right tnum">
        <div>{money(c.spend, c.spendCurrency)}</div>
        <DeltaBadge delta={c.deltas.spend} good="neutral" />
      </td>
      <td className="px-4 py-3 text-right tnum">
        <div>{num(c.conversions)}</div>
        <DeltaBadge delta={c.deltas.conversions} good="up" />
      </td>
      <td className="px-4 py-3 text-right tnum">
        <div>{c.costPerConversion == null ? "—" : money(c.costPerConversion, c.spendCurrency, 2)}</div>
        <DeltaBadge delta={c.deltas.costPerConversion} good="down" />
      </td>
      <td className="px-4 py-3 text-right tnum">
        {c.roas == null ? (
          <span className="text-xs text-muted">{c.revenueLogged ? "—" : "not logged"}</span>
        ) : (
          <span className="font-medium">
            {roasX(c.roas)}
            {c.roasEstimated && (
              <span
                className="ml-1 text-[10px] font-normal text-warning"
                title="Estimated — prorated from a monthly/weekly revenue figure; log daily revenue for exact ROAS"
              >
                est
              </span>
            )}
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <FlagBadge flag={c.flag} reasons={c.reasons} />
      </td>
    </tr>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  right,
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: "asc" | "desc";
  right?: boolean;
}) {
  return (
    <th className={`px-4 py-3 font-medium ${right ? "text-right" : ""}`}>
      <button
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-ink ${active ? "text-ink" : ""}`}
      >
        {children}
        {active && <span className="text-[9px]">{dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  );
}

function cmp(a: ClientSummary, b: ClientSummary, key: SortKey): number {
  if (key === "name") return a.name.localeCompare(b.name);
  const av = a[key] ?? -Infinity;
  const bv = b[key] ?? -Infinity;
  return (av as number) - (bv as number);
}
